import { InternalQuestionGroups, QuestionType } from '@trupropel/vcp-commons';

import * as DateUtils from '../utils/date.util';

export const IGNORE_ALL_INDEX = -1;

export interface VCPQuestionValue {
  text: string | undefined | (string | undefined)[];
  group: string;
  extra?: (string | null)[] | null | undefined;
  code?: string;
}

export interface GroupByProps {
  [key: string]: {
    id: number;
    title: string;
    groupBy: GROUPBY;
    keys?: string[];
    valueFn?: (question: any, value: any) => boolean;
  };
}

export interface StructureGroupByProps {
  type: string;
  allTitle: string;
  allIndex: number; // Index where the "all questions" section will appear. Must be coherent with groupByConfig indexes.
  groupBy: GroupByProps;
}

export interface VCPQuestion {
  title: string;
  type: string;
  key: string;
  category?: string;
  value: VCPQuestionValue | VCPQuestionValue[];
}

export interface VCPQuestionGroup {
  title?: string;
  key: string;
  questions: VCPQuestion[];
}

export enum GROUPBY {
  KEY = 'key',
  VALUE = 'value',
  CATEGORY = 'category',
}

const checkGroupValue = (answer: VCPQuestion, valueFn: (...args) => boolean) => {
  let valueResult = false;
  if (Array.isArray(answer.value)) {
    valueResult = answer.value.some((val) => valueFn(answer, val));
  } else {
    valueResult = valueFn(answer, answer.value);
  }
  return valueResult;
};

const groupCheck = (
  groupByConfig: GroupByProps,
  answer: VCPQuestion | VCPQuestionGroup,
  groupBy: GROUPBY,
  fn: (...args) => boolean,
  prop: string,
) => {
  const belongsTo = {};
  const addIfBelongs = (fn, id) => {
    if (fn()) {
      belongsTo[id] = true;
    }
  };
  for (const groupkey in groupByConfig) {
    const group = groupByConfig[groupkey];
    if (group && group.groupBy === groupBy) {
      addIfBelongs(() => fn(answer, group[prop]), group.id);
    }
  }
  return belongsTo;
};

const checkGroupKey = (answer: VCPQuestion, keys: string[]) => {
  return keys.includes(answer.key);
};

const checkGroupCategory = (answer: VCPQuestion, keys: string[]) => {
  return keys.includes(answer.category);
};

const groupCheckValues = (groupByConfig: GroupByProps, answer: VCPQuestion) => {
  return groupCheck(groupByConfig, answer, GROUPBY.VALUE, checkGroupValue, 'valueFn');
};

const groupCheckKeys = (groupByConfig: GroupByProps, answer: VCPQuestion) => {
  return groupCheck(groupByConfig, answer, GROUPBY.KEY, checkGroupKey, 'keys');
};

const groupCheckCategory = (groupByConfig: GroupByProps, answer: VCPQuestion) => {
  return groupCheck(groupByConfig, answer, GROUPBY.CATEGORY, checkGroupCategory, 'keys');
};

export const parseSingleAnswer = (group: string, question: any, locale: string) => {
  const answer = question?.answer;
  if (answer && answer?.length > 0) {
    let text = answer[0]?.value;
    switch (question.type) {
      case QuestionType.date:
        text = DateUtils.toDate(answer[0]?.value, locale);
        break;
      case QuestionType.currency:
      case QuestionType.units:
        text = `${answer[0]?.value} ${answer[0]?.extra}`;
        break;
    }

    const parsedAnswer: VCPQuestion = {
      title: question.title,
      type: question.type,
      key: question.key,
      value: {
        text,
        group,
        extra: answer[0]?.extra,
        code: answer[0]?.code,
      },
    };
    return parsedAnswer;
  }
  return null;
};

export const parseMultiAnswer = (group: string, question: any) => {
  const answer = question?.answer;
  if (answer && answer?.length > 0) {
    const parsedAnswer: VCPQuestion = {
      title: question.title,
      key: question.key,
      type: question.type,
      value: answer?.map((item: any) => {
        const val: VCPQuestionValue = { text: item.value, group, code: item.code };
        return val;
      }),
    };
    return parsedAnswer;
  }
  return null;
};

export const parseMediaNote = (group: string, question: any) => {
  const answer = question?.answer;
  if (answer && answer?.length > 0) {
    const parsedAnswer: VCPQuestion = {
      title: question.title,
      key: question.key,
      type: question.type,
      value: { text: answer?.[0]?.value, group, extra: answer?.[0]?.extra },
    };
    return parsedAnswer;
  }
  return null;
};

export const parseMedia = (group: string, question: any) => {
  const answer = question?.answer;
  if (answer && answer?.length > 0) {
    const parsedAnswer: VCPQuestion = {
      title: question.title,
      key: question.key,
      type: question.type,
      value: { text: answer?.map((pic) => pic?.value), group },
    };
    return parsedAnswer;
  }
  return null;
};

const addToGroup = (questionGroup, groups, groupsThatBelongs) => {
  if (groupsThatBelongs && Object.keys(groupsThatBelongs).length > 0) {
    for (const groupId in groupsThatBelongs) {
      if (groups && groups[groupId]) {
        const group = groups[groupId];
        group.questionGroups.push(questionGroup);
      }
    }
  }
};

const generateGroupsResult = (allIndex: number, groups: any, allTitle: string) => {
  const generatedGroupResult = {
    [allIndex]: { title: allTitle, questionGroups: [], questionGroupTitleIcon: undefined },
  };
  for (const groupkey in groups) {
    const group = groups[groupkey];
    generatedGroupResult[group.id] = {
      title: group.title,
      questionGroups: [],
      questionGroupTitleIcon: group.questionGroupTitleIcon,
    };
  }
  return generatedGroupResult;
};

const arrayStringToHash = (array) => {
  const hashValues = {};
  for (const item of array) {
    hashValues[item] = true;
  }
  return hashValues;
};

export const parseAnswers = (
  questionGroups: any,
  locale: string,
  allIndex: number,
  allTitle: string,
  groupByConfig: GroupByProps,
  filterByQuestionGroupKeys: string[],
  category?: string,
) => {
  const groups = generateGroupsResult(allIndex, groupByConfig, allTitle);
  const applyFilterByKeys = filterByQuestionGroupKeys?.length > 0;
  const filterBykeys = arrayStringToHash(filterByQuestionGroupKeys);
  const filterByKeysFn = (key: string) => {
    if (applyFilterByKeys) {
      return filterBykeys[key];
    }
    return true;
  };
  if (questionGroups) {
    for (const questionGroup of questionGroups) {
      /* If a category is sent then it should match with the question group one */
      if (category && questionGroup.category !== category) continue;

      let belongsTo = {};
      const questionGroupKey = questionGroup?.key || '';
      if (questionGroup && questionGroup.questions && filterByKeysFn(questionGroupKey)) {
        const parsedGroupQuestions = [];
        for (const question of questionGroup.questions) {
          if (question && question.answer) {
            let parsedQuestion;
            switch (question.type) {
              case QuestionType.singleselect:
                parsedQuestion = parseSingleAnswer(questionGroupKey, question, locale);
                break;
              case QuestionType.address:
              case QuestionType.note:
              case QuestionType.units:
              case QuestionType.currency:
                parsedQuestion = parseSingleAnswer(questionGroupKey, question, locale);
                break;
              case QuestionType.multiselect:
                parsedQuestion = parseMultiAnswer(questionGroupKey, question);
                break;
              case QuestionType.singlemultiselect:
                parsedQuestion = parseMultiAnswer(questionGroupKey, question);
                break;
              case QuestionType.medianote:
                parsedQuestion = parseMediaNote(questionGroupKey, question);
                break;
              case QuestionType.media:
              case QuestionType.gallery:
                parsedQuestion = parseMedia(questionGroupKey, question);
                break;
            }
            if (parsedQuestion) {
              const groupsThatBelongsQuestion = groupCheckValues(groupByConfig, parsedQuestion);
              belongsTo = { ...belongsTo, ...groupsThatBelongsQuestion };
              parsedGroupQuestions.push(parsedQuestion);
            }
          }
        }
        const parsedQuestionGroup = {
          title: questionGroup.title,
          key: questionGroupKey,
          questions: parsedGroupQuestions,
        };
        const groupsThatBelongs = groupCheckKeys(groupByConfig, questionGroup);

        // Find the groups that matches the category of the question group
        const groupsByCategory = groupCheckCategory(groupByConfig, questionGroup);

        belongsTo = { ...belongsTo, ...groupsThatBelongs, ...groupsByCategory };
        if (Object.keys(belongsTo).length === 0 && allIndex !== IGNORE_ALL_INDEX) {
          belongsTo = { [allIndex]: true };
        }
        addToGroup(parsedQuestionGroup, groups, belongsTo);
      }
    }
  }
  return Object.keys(groups).map((key) => groups[key]);
};

export const getAnswersNumberInQuestionGroup = (gp: InternalQuestionGroups) => {
  return gp?.questions?.filter((q) => q.answer !== null && q.visible)?.length || 0;
};
